/**
 * WYSIWYG Rich Text Editor Component
 * 
 * Provides a full-featured rich text editing experience with inline editing
 * and all formatting options available through a toolbar
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box, useTheme } from '@mui/material';
import './WYSIWYGRichTextEditor.css';

// Register custom fonts with Quill
const Font = Quill.import('formats/font') as any;
Font.whitelist = [
  false, // Default (Sans Serif)
  'serif',
  'monospace', 
  'arial',
  'georgia',
  'times-new-roman',
  'verdana',
  'helvetica',
  'courier-new',
  'trebuchet-ms',
  'tahoma',
  'impact',
  'comic-sans-ms',
  'palatino',
  'garamond',
  'bookman',
  'avant-garde'
];
Quill.register(Font, true);

// Register custom sizes with Quill
const Size = Quill.import('formats/size') as any;
Size.whitelist = ['8px', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '30px', '36px', '48px', '60px', '72px', '96px'];
Quill.register(Size, true);

interface WYSIWYGRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readonly?: boolean;
  isSelected?: boolean;
  minHeight?: number;
  // Container styling properties (handled by property panel)
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderStyle?: 'solid' | 'dashed' | 'dotted' | 'none';
  borderRadius?: number;
  padding?: number;
}

const WYSIWYGRichTextEditor: React.FC<WYSIWYGRichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  readonly = false,
  isSelected = false,
  minHeight = 20,
  backgroundColor,
  borderColor = '#e0e0e0',
  borderWidth = 0,
  borderStyle = 'solid',
  borderRadius = 4,
  padding = 0
}) => {
  const theme = useTheme();
  const quillRef = useRef<ReactQuill>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Quill toolbar configuration with all formatting options
  const modules = useMemo(() => ({
    toolbar: !readonly ? {
      container: [
      // Text formatting
      [{ 'font': [
        '', // Default font (will show as Sans Serif)
        'serif',
        'monospace',
        'arial',
        'georgia', 
        'times-new-roman',
        'verdana',
        'helvetica',
        'courier-new',
        'trebuchet-ms',
        'tahoma',
        'impact',
        'comic-sans-ms',
        'palatino',
        'garamond',
        'bookman',
        'avant-garde'
      ] }], // Font family
      [{ 'size': ['8px', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '30px', '36px', '48px', '60px', '72px', '96px'] }], // Font size
      
      // Text styling
      ['bold', 'italic', 'underline', 'strike'], // Basic formatting
      [{ 'color': [] }, { 'background': [] }], // Text and background color
      
      // Text alignment (explicitly specify options for better visibility)
      [{ 'align': ['', 'center', 'right', 'justify'] }],
      
      // Lists and indentation
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      
      // Special formatting
      [{ 'script': 'sub'}, { 'script': 'super' }], // Subscript/superscript
      ['blockquote', 'code-block'],
      
      // Headers
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      
      // Links only (removed image and video)
      ['link'],
      
      // Clear formatting
      ['clean']
      ],
      handlers: {}
    } : false,
    clipboard: {
      matchVisual: false // Preserve formatting when pasting
    }
  }), [readonly]);

  // Quill formats configuration
  const formats = [
    'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align',
    'list', 'indent',
    'script',
    'blockquote', 'code-block',
    'header',
    'link'
  ];

  // Container styling based on properties
  const effectiveBorderColor = isFocused && borderWidth > 0
    ? theme.palette.primary.main
    : borderColor;
  const effectiveBorderWidth = isFocused && borderWidth > 0 ? 2 : borderWidth;
  const containerStyle = {
    backgroundColor: backgroundColor || 'transparent',
    border: borderStyle !== 'none' ? `${effectiveBorderWidth}px ${borderStyle} ${effectiveBorderColor}` : 'none',
    borderRadius: `${borderRadius}px`,
    padding: `${padding}px`,
    minHeight: `${minHeight}px`,
    transition: 'border-color 0.2s ease, border-width 0.2s ease',
    outline: isSelected && !isFocused ? `2px solid ${theme.palette.primary.main}` : 'none',
    outlineOffset: '2px'
  };

  // Handle focus events
  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  // Custom styles for the Quill editor and tooltips
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      
      // Set minimum height for the editor content area
      const editorElement = editor.root as HTMLElement;
      editorElement.style.minHeight = `${minHeight - 42}px`; // Subtract toolbar height
      
      // Apply custom styles for better integration
      if (!readonly) {
        editorElement.style.fontFamily = theme.typography.body1.fontFamily || 'Roboto, sans-serif';
        
        // Add tooltips to toolbar buttons
        const toolbar = editor.container.previousSibling as HTMLElement;
        if (toolbar) {
          // Add tooltips for formatting buttons
          const boldBtn = toolbar.querySelector('.ql-bold');
          if (boldBtn) boldBtn.setAttribute('title', 'Bold (Ctrl+B)');
          
          const italicBtn = toolbar.querySelector('.ql-italic');
          if (italicBtn) italicBtn.setAttribute('title', 'Italic (Ctrl+I)');
          
          const underlineBtn = toolbar.querySelector('.ql-underline');
          if (underlineBtn) underlineBtn.setAttribute('title', 'Underline (Ctrl+U)');
          
          const strikeBtn = toolbar.querySelector('.ql-strike');
          if (strikeBtn) strikeBtn.setAttribute('title', 'Strikethrough');
          
          // Color and background
          const colorBtn = toolbar.querySelector('.ql-color');
          if (colorBtn) colorBtn.setAttribute('title', 'Text Color');
          
          const bgBtn = toolbar.querySelector('.ql-background');
          if (bgBtn) bgBtn.setAttribute('title', 'Background Color');
          
          // Lists
          const orderedBtn = toolbar.querySelector('.ql-list[value="ordered"]');
          if (orderedBtn) orderedBtn.setAttribute('title', 'Numbered List');
          
          const bulletBtn = toolbar.querySelector('.ql-list[value="bullet"]');
          if (bulletBtn) bulletBtn.setAttribute('title', 'Bullet List');
          
          // Indentation
          const indentLeftBtn = toolbar.querySelector('.ql-indent[value="-1"]');
          if (indentLeftBtn) indentLeftBtn.setAttribute('title', 'Decrease Indent');
          
          const indentRightBtn = toolbar.querySelector('.ql-indent[value="+1"]');
          if (indentRightBtn) indentRightBtn.setAttribute('title', 'Increase Indent');
          
          // Script
          const subBtn = toolbar.querySelector('.ql-script[value="sub"]');
          if (subBtn) subBtn.setAttribute('title', 'Subscript');
          
          const superBtn = toolbar.querySelector('.ql-script[value="super"]');
          if (superBtn) superBtn.setAttribute('title', 'Superscript');
          
          // Block formats
          const blockquoteBtn = toolbar.querySelector('.ql-blockquote');
          if (blockquoteBtn) blockquoteBtn.setAttribute('title', 'Blockquote');
          
          const codeBtn = toolbar.querySelector('.ql-code-block');
          if (codeBtn) codeBtn.setAttribute('title', 'Code Block');
          
          // Link
          const linkBtn = toolbar.querySelector('.ql-link');
          if (linkBtn) linkBtn.setAttribute('title', 'Insert Link');
          
          // Clean formatting
          const cleanBtn = toolbar.querySelector('.ql-clean');
          if (cleanBtn) cleanBtn.setAttribute('title', 'Clear Formatting');
          
          // Alignment
          const alignLeft = toolbar.querySelector('.ql-align');
          if (alignLeft) alignLeft.setAttribute('title', 'Align Left');
          
          const alignCenter = toolbar.querySelector('.ql-align[value="center"]');
          if (alignCenter) alignCenter.setAttribute('title', 'Align Center');
          
          const alignRight = toolbar.querySelector('.ql-align[value="right"]');
          if (alignRight) alignRight.setAttribute('title', 'Align Right');
          
          const alignJustify = toolbar.querySelector('.ql-align[value="justify"]');
          if (alignJustify) alignJustify.setAttribute('title', 'Justify');
          
          // Dropdowns
          const fontPicker = toolbar.querySelector('.ql-font');
          if (fontPicker) fontPicker.setAttribute('title', 'Font Family');
          
          const sizePicker = toolbar.querySelector('.ql-size');
          if (sizePicker) sizePicker.setAttribute('title', 'Font Size');
          
          const headerPicker = toolbar.querySelector('.ql-header');
          if (headerPicker) headerPicker.setAttribute('title', 'Heading Level');
        }
      }
    }
  }, [minHeight, theme, readonly]);

  // Clamp Quill link tooltip to stay within the editor bounds and viewport
  useEffect(() => {
    if (!quillRef.current || readonly) return;

    const editor = quillRef.current.getEditor();
    const container = editor.container as HTMLElement;
    const tooltip = container.querySelector('.ql-tooltip') as HTMLElement | null;
    if (!tooltip) return;

    const clampTooltip = () => {
      if (tooltip.classList.contains('ql-hidden')) return;

      const containerWidth = container.offsetWidth;
      const tooltipWidth = tooltip.offsetWidth;
      const tooltipHeight = tooltip.offsetHeight;

      // Clamp horizontal: keep tooltip within the container width
      const currentLeft = parseFloat(tooltip.style.left) || 0;
      const maxLeft = containerWidth - tooltipWidth - 4;
      const clampedLeft = Math.max(4, Math.min(currentLeft, maxLeft));
      if (Math.abs(clampedLeft - currentLeft) > 0.5) {
        tooltip.style.left = `${clampedLeft}px`;
      }

      // Flip above selection if tooltip would overflow bottom of viewport
      const containerRect = container.getBoundingClientRect();
      const currentTop = parseFloat(tooltip.style.top) || 0;
      const absoluteBottom = containerRect.top + currentTop + tooltipHeight;
      if (absoluteBottom > window.innerHeight - 8) {
        const selection = editor.getSelection();
        const bounds = selection ? editor.getBounds(selection.index) : null;
        if (bounds) {
          const aboveTop = bounds.top - tooltipHeight - 8;
          if (Math.abs(aboveTop - currentTop) > 0.5) {
            tooltip.style.top = `${aboveTop}px`;
          }
        }
      }
    };

    const observer = new MutationObserver(clampTooltip);
    observer.observe(tooltip, { attributes: true, attributeFilter: ['style', 'class'] });

    return () => observer.disconnect();
  }, [readonly]);

  if (readonly) {
    // Render mode - just display the HTML content
    return (
      <Box
        sx={containerStyle}
        dangerouslySetInnerHTML={{ __html: value || '<p style="color: #999;">No content</p>' }}
        className="wysiwyg-readonly-content"
      />
    );
  }

  return (
    <Box sx={containerStyle} className="wysiwyg-editor-container">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      />
    </Box>
  );
};

export default WYSIWYGRichTextEditor;